import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SectionDecor } from "@/components/Decor";
import { Calendar, MapPin, Users, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { WorkshopCard } from "@/components/WorkshopCard";
import ReservationDialog from "@/components/ReservationDialog";
import { LEVELS, COMPETENCES, LEVEL_STYLES } from "@/lib/constants";

export default function Activities() {
  const [type, setType] = useState("ateliers");
  const [workshops, setWorkshops] = useState([]);
  const [stages, setStages] = useState([]);
  const [level, setLevel] = useState("all");
  const [competence, setCompetence] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (type !== "ateliers") return;
    setLoading(true);
    const params = {};
    if (level !== "all") params.level = level;
    if (competence !== "all") params.competence = competence;
    api
      .get("/workshops", { params })
      .then((r) => setWorkshops(r.data))
      .catch(() => setWorkshops([]))
      .finally(() => setLoading(false));
  }, [type, level, competence]);

  const changeType = (t) => {
    setType(t);
    setLevel("all");
    setCompetence("all");
  };

  const resetFilters = () => {
    setLevel("all");
    setCompetence("all");
  };

  useEffect(() => {
    if (type !== "stages") return;
    setLoading(true);
    api.get("/academy").then((r) => setStages(r.data)).finally(() => setLoading(false));
  }, [type]);

  return (
    <div>
      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 to-white py-16">
        <SectionDecor />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Nos activités</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-2 max-w-3xl">
            Des ateliers clés en main pour vos classes
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl">
            Filtrez par type, niveau scolaire ou compétence du référentiel FMTTN
            pour trouver l'activité parfaite.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Type filters */}
        <div className="mb-4">
          <p className="text-sm font-bold text-slate-500 mb-3">Type</p>
          <div className="flex flex-wrap gap-2">
            <FilterPill active={type === "ateliers"} onClick={() => changeType("ateliers")} testid="filter-type-ateliers">
              Ateliers
            </FilterPill>
            <FilterPill active={type === "stages"} onClick={() => changeType("stages")} testid="filter-type-stages">
              Stages
            </FilterPill>
          </div>
        </div>

        {type === "ateliers" && (
          <>
            {/* Level filters */}
            <div className="mb-4">
              <p className="text-sm font-bold text-slate-500 mb-3">Niveau</p>
              <div className="flex flex-wrap gap-2">
                <FilterPill active={level === "all"} onClick={() => setLevel("all")} testid="filter-level-all">
                  Tous
                </FilterPill>
                {LEVELS.map((l) => (
                  <FilterPill
                    key={l}
                    active={level === l}
                    onClick={() => setLevel(l)}
                    testid={`filter-level-${l}`}
                  >
                    {LEVEL_STYLES[l].label}
                  </FilterPill>
                ))}
              </div>
            </div>

            {/* Competence filters */}
            <div className="mb-10">
              <p className="text-sm font-bold text-slate-500 mb-3">Compétence</p>
              <div className="flex flex-wrap gap-2">
                <FilterPill active={competence === "all"} onClick={() => setCompetence("all")} testid="filter-comp-all">
                  Toutes
                </FilterPill>
                {COMPETENCES.map((c) => (
                  <FilterPill
                    key={c}
                    active={competence === c}
                    onClick={() => setCompetence(c)}
                    testid={`filter-comp-${c}`}
                  >
                    {c}
                  </FilterPill>
                ))}
              </div>
            </div>
          </>
        )}

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-80 bg-slate-100 rounded-3xl animate-pulse" />
            ))}
          </div>
        ) : type === "ateliers" ? (
          workshops.length === 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16" data-testid="workshops-empty">
              <p className="text-slate-500">Aucun atelier ne correspond à cette combinaison de filtres.</p>
              {(level !== "all" || competence !== "all") && (
                <button
                  onClick={resetFilters}
                  data-testid="reset-filters"
                  className="mt-4 inline-flex items-center px-5 py-2.5 rounded-full bg-sky-500 text-white text-sm font-bold hover:bg-sky-600 transition-colors"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </motion.div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="workshops-grid">
              {workshops.map((w, i) => (
                <WorkshopCard key={w.id} workshop={w} index={i} />
              ))}
            </div>
          )
        ) : stages.length === 0 ? (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-slate-500 py-16">
            Aucun stage disponible pour le moment.
          </motion.p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="stages-grid">
            {stages.map((s, i) => (
              <StageCard key={s.id} stage={s} index={i} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StageCard({ stage: s, index = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: (index % 3) * 0.08 }}
      className="bg-white rounded-3xl border border-slate-100 shadow-sm p-7 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] flex flex-col"
      data-testid={`stage-card-${s.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold mb-3">{s.period}</span>
          <h3 className="font-heading font-extrabold text-xl text-slate-900">{s.title}</h3>
        </div>
        <span className="font-heading font-black text-2xl text-sky-500 shrink-0">{s.price.toFixed(0)} €</span>
      </div>
      <p className="text-sm text-slate-500 mt-3 flex-1">{s.description}</p>
      <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
        <span className="inline-flex items-center gap-2 text-slate-600"><Calendar className="w-4 h-4 text-sky-500" /> {s.dates}</span>
        <span className="inline-flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4 text-sky-500" /> {s.location}</span>
        <span className="inline-flex items-center gap-2 text-slate-600"><Users className="w-4 h-4 text-sky-500" /> {s.ages}</span>
        <span className="inline-flex items-center gap-2 text-slate-600"><Tag className="w-4 h-4 text-sky-500" /> {s.spots} places</span>
      </div>
      <div className="mt-6 flex flex-col gap-2">
        <Link to={`/academie/${s.id}`} data-testid={`stage-detail-link-${s.id}`} className="w-full text-center py-3 rounded-full border border-slate-200 text-slate-700 font-bold hover:border-sky-400 hover:text-sky-600 transition-colors">
          Voir le programme
        </Link>
        <ReservationDialog
          kind="stage"
          itemId={s.id}
          itemTitle={s.title}
          trigger={
            <button data-testid={`reserve-stage-${s.id}`} className="w-full py-3 rounded-full bg-slate-900 text-white font-bold hover:bg-sky-600 transition-colors">
              Réserver une place
            </button>
          }
        />
      </div>
    </motion.div>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 rounded-full text-sm font-bold transition-[transform,background-color] hover:scale-105 ${
        active ? "bg-sky-500 text-white shadow-lg shadow-sky-500/30" : "bg-white border border-slate-200 text-slate-600 hover:border-sky-300"
      }`}
    >
      {children}
    </button>
  );
}
